import { Link } from "react-router-dom";
import { Reveal, SectionLabel } from "@/components/motion";
import { IMAGES } from "@/lib/data";

const COVERAGE = [
  "Western Canada Hub (Calgary / Edmonton)",
  "Trans-Canada Highway Lanes",
  "Cross-Border U.S. Corridors",
  "Northern Alberta Industrial & Resource Sites",
];

const VALUES = [
  { title: "Professionalism", desc: "Honest, respectful dealings with shippers and carriers." },
  { title: "Communication", desc: "Proactive updates. No chasing." },
  { title: "Reliability", desc: "Careful carrier selection and follow-through." },
  { title: "Relationships", desc: "Long-term partnerships over one-off transactions." },
];

export default function About() {
  return (
    <main className="pt-16" data-testid="about-page">
      <section className="relative overflow-hidden border-b border-line">
        <img src={IMAGES.calgary} alt="Calgary, Alberta" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/70 to-asphalt/40" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-8">
          <SectionLabel>// About ELT</SectionLabel>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-bright sm:text-5xl lg:text-6xl">
            Independent. Coordinated. <span className="text-hazard">Accountable.</span>
          </h1>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <SectionLabel>// Who We Are</SectionLabel>
          <h2 className="mt-3 font-display text-3xl font-extrabold uppercase text-bright sm:text-4xl">A Calgary brokerage built on follow-through</h2>
          <p className="mt-6 text-base leading-relaxed text-[#9CA3AF]">
            ELT Freight &amp; Logistics is a Calgary-based freight brokerage connecting businesses that need transportation with independent carriers and owner-operators across Canada and North American markets.
          </p>
          <p className="mt-4 text-base leading-relaxed text-[#9CA3AF]">
            We coordinate the entire process — load requirements, carrier sourcing, booking, tracking and delivery follow-up — so you deal with one professional point of contact instead of many.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <SectionLabel>// Where We Operate</SectionLabel>
          <h2 className="mt-3 font-display text-3xl font-extrabold uppercase text-bright sm:text-4xl">Centre of the Western corridors</h2>
          <p className="mt-6 text-base leading-relaxed text-[#9CA3AF]">
            Based in Calgary, Alberta — at the centre of Western Canada's energy, construction and industrial corridors. Whether a single urgent load or regular weekly freight, we scale coordination to fit.
          </p>
          <div className="mt-8 flex flex-col">
            {COVERAGE.map((c, i) => (
              <div key={c} className="flex items-center gap-4 border-b border-line py-4" data-testid={`coverage-${i}`}>
                <span className="font-mono text-xs text-hazard">0{i + 1}</span>
                <span className="font-display text-lg font-bold uppercase text-bright">{c}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="border-t border-line bg-panel/40">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <Reveal>
            <SectionLabel>// How We Work</SectionLabel>
            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase text-bright sm:text-4xl">The ELT operating code</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="group h-full border border-line bg-asphalt p-7 transition-colors duration-300 hover:bg-panel">
                  <p className="font-mono text-xs font-bold text-hazard">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-xl font-bold uppercase text-bright">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#9CA3AF]">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link to="/contact" data-testid="about-cta-link" className="inline-block bg-hazard px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-asphalt transition-colors hover:bg-hazard-dark">
              Start a Conversation
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
